// =============================================================================
// useUserManagementData
//
// Responsibility: subscribe to Redux state + trigger server-side paginated API
// fetches. Implements proper page-by-page pagination with server-side sort and
// filter support.
//
// Key behaviors:
//   - Single page fetch per render cycle driven by { currentPage, filters, sortConfig }
//   - Changing sort or column filters resets to page 0 (handled in the slice)
//   - Search input is debounced 350ms before triggering an API call
//   - All filtering (location, accessLevel, activeStatus) and sorting are sent
//     to the backend so they apply to the full dataset, not just the current page
// =============================================================================

import { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store';
import type { RootState } from '../../../../../store';
import {
  selectUsers,
  selectFilteredUsers,
  selectSelectedUserIds,
  selectFilters,
  selectSortConfig,
  selectSelectedUser,
  selectNewUsersCount,
  selectActiveUsersCount,
  selectInactiveUsersCount,
  selectLoading,
  selectError,
  selectCurrentPage,
  selectTotalCount,
  selectTotalPages,
  selectPageSize,
  setUsers,
  setLoading,
  setError,
  setCurrentPage,
  setPaginationMeta,
  setPageSize,
} from '../../../../../store/slices/userManagementSlice';
import { userManagementApi } from '../apiRoute';
import type { User, FilterState, SortConfig } from '../userManagementTypes';

/** Fallback page size if not set in Redux / .env */
export const DEFAULT_PAGE_SIZE = parseInt(process.env.USER_MGMT_PAGE_SIZE ?? '10', 10) || 10;

/** Valid page size options shown in the UI dropdown */
export const PAGE_SIZE_OPTIONS = [10, 50, 75, 100] as const;

/** @deprecated use pageSize from the hook return value instead */
export const PAGE_SIZE = DEFAULT_PAGE_SIZE;

export interface UseUserManagementDataReturn {
  users: User[];
  filteredUsers: User[];
  selectedUserIds: string[];
  filters: FilterState;
  sortConfig: SortConfig;
  selectedUser: User | null;
  newUsersCount: number;
  activeUsersCount: number;
  inactiveUsersCount: number;
  isLoading: boolean;
  error: string | null;
  // Pagination
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  goToPage: (page: number) => void;
  changePageSize: (size: number) => void;
  /** Re-fetch the current page from the server. Call after any mutation. */
  refreshCurrentPage: () => void;
  dispatch: ReturnType<typeof useAppDispatch>;
}

export const useUserManagementData = (): UseUserManagementDataReturn => {
  const dispatch = useAppDispatch();

  // ── Selectors ──────────────────────────────────────────────────────────────
  const users = useSelector(selectUsers);
  const filteredUsers = useSelector(selectFilteredUsers);
  const selectedUserIds = useSelector(selectSelectedUserIds);
  const filters = useSelector(selectFilters);
  const sortConfig = useSelector(selectSortConfig);
  const selectedUser = useSelector(selectSelectedUser);
  const newUsersCount = useSelector(selectNewUsersCount);
  const activeUsersCount = useSelector(selectActiveUsersCount);
  const inactiveUsersCount = useSelector(selectInactiveUsersCount);
  const isLoading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const currentPage = useSelector(selectCurrentPage);
  const pageSize = useSelector(selectPageSize);
  const totalCount = useSelector(selectTotalCount);
  const totalPages = useSelector(selectTotalPages);

  // ── Request version guard ──────────────────────────────────────────────────
  const requestVersionRef = useRef(0);
  const isFetchingRef = useRef(false);

  // ── Debounce ref for search ────────────────────────────────────────────────
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevSearchRef = useRef<string>(filters.search);

  // ── Core fetch function ───────────────────────────────────────────────────
  const fetchPage = useCallback(
    async (page: number, pageSizeSnap: number, filtersSnap: FilterState, sortSnap: SortConfig, version: number) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      try {
        dispatch(setLoading(true));
        dispatch(setError(null));

        const { column, direction } = sortSnap;

        // 'ALL' is a literal DB value (used for Super User / COE / Developer etc.).
        // The backend treats 'ALL' as a reserved wildcard in the location filter and
        // returns 0 results, so we strip it from the API params and apply a
        // client-side post-filter on the returned page instead.
        const hasAllFilter = filtersSnap.location.includes('ALL');
        const locationsForApi = filtersSnap.location.filter(l => l !== 'ALL');

        // Backend stores activeStatus as true (Active) / false (Inactive), not as strings.
        const activeStatusForApi = filtersSnap.activeStatus.map(s =>
          s === 'Active' ? 'true' : s === 'Inactive' ? 'false' : s
        );

        const result = await userManagementApi.getPage(page, pageSizeSnap, {
          search: filtersSnap.search.trim() || undefined,
          sortBy: column ?? undefined,
          sortOrder: column ? direction : undefined,
          // When 'ALL' is the only selection, send no location param so the backend
          // returns every user (we then post-filter below). When combined with other
          // locations, send only the real location names.
          location: locationsForApi.length ? locationsForApi : undefined,
          accessLevel: filtersSnap.accessLevel.length ? filtersSnap.accessLevel : undefined,
          activeStatus: activeStatusForApi.length ? activeStatusForApi : undefined,
        });

        // Discard stale results
        if (version !== requestVersionRef.current) {
          console.log(`[useUserManagementData] Discarding stale results for page ${page}`);
          return;
        }

        // Client-side post-filter for the literal 'ALL' location value.
        // Keeps rows that match ANY of the selected location filters:
        //   - 'ALL'  → user.location contains the GLOBAL-scope display text 'All Locations'
        //   - others → already handled server-side; they pass through unchanged
        const selectedLocations = filtersSnap.location;
        const usersToShow = hasAllFilter
          ? result.users.filter(u =>
              selectedLocations.some(sel =>
                sel === 'ALL'
                  ? u.location.some(l => l === 'All Locations')
                  : u.location.includes(sel)
              )
            )
          : result.users;

        dispatch(setUsers(usersToShow));
        // NOTE: setUsers already sets both state.users and state.filteredUsers.
        // Do NOT call setFilteredUsers here — it would overwrite with the
        // un-deduplicated array and cause duplicate rows in the table.
        dispatch(setPaginationMeta({ totalCount: result.totalCount, totalPages: result.totalPages }));

        console.log(
          `[useUserManagementData] Page ${page} loaded: ${result.users.length} rows,`,
          `total=${result.totalCount}, pages=${result.totalPages}`
        );
      } catch (err: any) {
        if (version !== requestVersionRef.current) return;
        console.error('[useUserManagementData] Error fetching page:', err);
        dispatch(setUsers([]));
        dispatch(setError(err.message || 'Failed to load users'));
      } finally {
        if (version === requestVersionRef.current) {
          dispatch(setLoading(false));
        }
        isFetchingRef.current = false;
      }
    },
    [dispatch]
  );

  // ── Effect: re-fetch whenever page / filters / sort change ────────────────
  useEffect(() => {
    // Cancel any pending search debounce timer
    if (searchDebounceRef.current !== null) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const version = ++requestVersionRef.current;
    isFetchingRef.current = false; // release lock for new request

    const searchChanged = prevSearchRef.current !== filters.search;
    prevSearchRef.current = filters.search;

    if (searchChanged) {
      // Clear stale rows immediately so the table doesn't show old results
      // while the debounced/new fetch is in-flight.
      dispatch(setUsers([]));
      dispatch(setPaginationMeta({ totalCount: 0, totalPages: 0 }));
    }

    if (searchChanged && filters.search.trim()) {
      // Debounce API call while the user is still typing
      searchDebounceRef.current = setTimeout(() => {
        searchDebounceRef.current = null;
        if (version !== requestVersionRef.current) return;
        fetchPage(currentPage, pageSize, filters, sortConfig, version);
      }, 350);
    } else {
      fetchPage(currentPage, pageSize, filters, sortConfig, version);
    }

    return () => {
      if (searchDebounceRef.current !== null) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filters, sortConfig]);

  // ── Navigate to a specific page ────────────────────────────────────────────
  const goToPage = useCallback(
    (page: number) => {
      dispatch(setCurrentPage(Math.max(0, page)));
    },
    [dispatch]
  );

  const changePageSize = useCallback(
    (size: number) => {
      dispatch(setPageSize(size));
    },
    [dispatch]
  );

  const refreshCurrentPage = useCallback(() => {
    const version = ++requestVersionRef.current;
    isFetchingRef.current = false;
    fetchPage(currentPage, pageSize, filters, sortConfig, version);
  }, [fetchPage, currentPage, pageSize, filters, sortConfig]);

  return {
    users,
    filteredUsers,
    selectedUserIds,
    filters,
    sortConfig,
    selectedUser,
    newUsersCount,
    activeUsersCount,
    inactiveUsersCount,
    isLoading,
    error,
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    goToPage,
    changePageSize,
    refreshCurrentPage,
    dispatch,
  };
};
